import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, Checkbox, ListItemText, Stack, CircularProgress, Tooltip, Link,
  FormControl, InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DownloadIcon from "@mui/icons-material/Download";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { addVaultRecord, getVaultRecords, deleteVaultRecord, updateVaultRecordAccess } from "../../services/vault.service";
import { getUser } from "../../services/user.service";
import { encryptData } from "../../utils/cryptoUtils";

const userLabel = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "user";

export default function VaultRecords() {
  const { masterId } = useParams();
  const navigate = useNavigate();
  const [master, setMaster] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [revealed, setRevealed] = useState({}); // `${recId}:${label}` -> true
  const [canEdit, setCanEdit] = useState(false); // admin → can delete records + grant access
  const [canAdd, setCanAdd] = useState(false); // any user → can add records
  const [users, setUsers] = useState([]); // for the per-record access dialog (admin)
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessRecId, setAccessRecId] = useState(null);
  const [accessUsers, setAccessUsers] = useState([]);

  const loadUsers = async () => {
    try {
      const res = await getUser({
        isManager: encryptData(false), search: encryptData(""),
        page: encryptData(1), limit: encryptData(1000), isEnabled: true,
      });
      setUsers(res?.data?.data || []);
    } catch (e) {
      console.error("load users failed", e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getVaultRecords(masterId);
      setMaster(res?.data?.master || null);
      setRecords(res?.data?.records || []);
      setCanEdit(!!res?.data?.canEdit);
      setCanAdd(!!res?.data?.canAdd);
      if (res?.data?.canEdit) loadUsers(); // admin → load users for access grants
    } catch (e) {
      console.error("load records failed", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [masterId]);

  const openAccess = (rec) => {
    setAccessRecId(rec._id);
    setAccessUsers((rec.allowedUsers || []).map(String));
    setAccessOpen(true);
  };
  const saveAccess = async () => {
    try {
      await updateVaultRecordAccess(accessRecId, accessUsers);
      setAccessOpen(false);
      load();
    } catch (e) {
      console.error("save access failed", e);
      alert("Failed to update access");
    }
  };

  const fields = master?.fields || [];

  const openAdd = () => {
    const init = {};
    fields.forEach((f) => {
      init[f.label] = f.type === "file" ? null : f.type === "multiselect" ? [] : "";
    });
    setForm(init);
    setOpen(true);
  };

  const submit = async () => {
    // required check (multiselect value is an array — empty [] is "missing")
    for (const f of fields) {
      const val = form[f.label];
      const empty = f.type === "multiselect" ? !(Array.isArray(val) && val.length) : !val;
      if (f.required && empty) return alert(`${f.label} is required`);
    }
    const fd = new FormData();
    fd.append("masterId", masterId);
    const scalar = {};
    fields.forEach((f) => {
      if (f.type === "file") {
        if (form[f.label]) fd.append(f.label, form[f.label]);
      } else {
        scalar[f.label] = form[f.label] ?? "";
      }
    });
    fd.append("values", JSON.stringify(scalar));
    setSaving(true);
    try {
      await addVaultRecord(fd);
      setOpen(false);
      load();
    } catch (e) {
      console.error("add record failed", e);
      alert("Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm("Delete this record?")) return;
    try { await deleteVaultRecord(rec._id); load(); } catch (e) { console.error(e); }
  };

  const renderCell = (f, rec) => {
    const v = rec.values?.[f.label];
    if (f.type === "multiselect") {
      const arr = Array.isArray(v) ? v : [];
      return arr.length ? arr.join(", ") : <span style={{ color: "#bbb" }}>—</span>;
    }
    if (v == null || v === "") return <span style={{ color: "#bbb" }}>—</span>;
    if (f.type === "file") {
      return v.url ? (
        <Link href={v.url} target="_blank" rel="noopener" underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <DownloadIcon fontSize="small" /> {v.name || "file"}
        </Link>
      ) : (v.name || "file");
    }
    if (f.type === "password") {
      const key = `${rec._id}:${f.label}`;
      const show = revealed[key];
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <span style={{ fontFamily: "monospace" }}>{show ? String(v) : "••••••••"}</span>
          <IconButton size="small" onClick={() => setRevealed((p) => ({ ...p, [key]: !show }))}>
            {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </Stack>
      );
    }
    return String(v);
  };

  const renderInput = (f) => {
    const val = form[f.label] ?? "";
    const set = (value) => setForm((p) => ({ ...p, [f.label]: value }));
    switch (f.type) {
      case "textarea":
        return <TextField label={f.label} value={val} onChange={(e) => set(e.target.value)}
          fullWidth multiline minRows={2} required={f.required} />;
      case "number":
        return <TextField label={f.label} type="number" value={val} onChange={(e) => set(e.target.value)}
          fullWidth required={f.required} />;
      case "date":
        return <TextField label={f.label} type="date" value={val} onChange={(e) => set(e.target.value)}
          fullWidth InputLabelProps={{ shrink: true }} required={f.required} />;
      case "password":
        return <TextField label={f.label} type="password" value={val} onChange={(e) => set(e.target.value)}
          fullWidth required={f.required} />;
      case "file":
        return (
          <Button variant="outlined" component="label" fullWidth
            sx={{ justifyContent: "flex-start", textTransform: "none" }}>
            {form[f.label]?.name || `Upload ${f.label}${f.required ? " *" : ""}`}
            <input hidden type="file" onChange={(e) => set(e.target.files?.[0] || null)} />
          </Button>
        );
      case "dropdown":
        return (
          <Select value={val} onChange={(e) => set(e.target.value)} fullWidth displayEmpty>
            <MenuItem value=""><em>{f.label}{f.required ? " *" : ""}</em></MenuItem>
            {(f.options || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
        );
      case "multiselect": {
        const arr = Array.isArray(val) ? val : [];
        return (
          <Select multiple value={arr} onChange={(e) => set(e.target.value)} fullWidth displayEmpty
            renderValue={(sel) => (sel.length ? sel.join(", ") : `${f.label}${f.required ? " *" : ""}`)}>
            {(f.options || []).map((o) => (
              <MenuItem key={o} value={o}>
                <Checkbox checked={arr.includes(o)} />
                <ListItemText primary={o} />
              </MenuItem>
            ))}
          </Select>
        );
      }
      default:
        return <TextField label={f.label} value={val} onChange={(e) => set(e.target.value)}
          fullWidth required={f.required} />;
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!master) return <Box sx={{ p: 3 }}><Typography>Master not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate("/home/vault")}><ArrowBackIcon /></IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{master.title}</Typography>
        </Stack>
        {canAdd && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>Add</Button>
        )}
      </Stack>

      {records.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center", color: "text.secondary" }}>
          {canAdd ? <>No records to show. Click <b>Add</b> — you'll see records you add (and any an admin grants you).</> : "No data yet."}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                {fields.map((f) => <TableCell key={f.label} sx={{ pl: "16px !important" }}><b>{f.label}</b></TableCell>)}
                {canEdit && <TableCell align="right"><b>Actions</b></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => (
                <TableRow key={rec._id} hover>
                  {fields.map((f) => <TableCell key={f.label} sx={{ pl: "16px !important" }}>{renderCell(f, rec)}</TableCell>)}
                  {canEdit && (
                    <TableCell align="right">
                      <Tooltip title={`Grant access${rec.allowedUsers?.length ? ` (${rec.allowedUsers.length})` : ""}`}>
                        <IconButton size="small" onClick={() => openAccess(rec)}>
                          <GroupAddIcon fontSize="small" sx={{ color: rec.allowedUsers?.length ? "#008080" : undefined }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete record">
                        <IconButton size="small" color="error" onClick={() => remove(rec)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add record dialog — dynamic form from master.fields */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add to {master.title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {fields.map((f) => <Box key={f.label}>{renderInput(f)}</Box>)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}
            sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-record access dialog (admin) — who can view THIS record */}
      <Dialog open={accessOpen} onClose={() => setAccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant access to this record</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Select users</InputLabel>
            <Select multiple label="Select users" value={accessUsers}
              onChange={(e) => setAccessUsers(e.target.value)}
              renderValue={(sel) => sel.map((id) => userLabel(users.find((u) => String(u._id) === id) || {})).join(", ")}>
              {users.map((u) => (
                <MenuItem key={u._id} value={String(u._id)}>
                  <Checkbox checked={accessUsers.includes(String(u._id))} />
                  <ListItemText primary={userLabel(u)} secondary={u.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Only selected users (plus admins) can view this record's data. Empty = admins only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAccess}
            sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
